import dayjs from 'dayjs'
import { Dispatch, SetStateAction } from 'react'
import { Controller, FormProvider, useFormContext } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { LoadingButton } from '@mui/lab'
import { Avatar, Box, Button, Divider, List, ListItem, ListItemAvatar, ListItemIcon, ListItemText, Stack } from '@mui/material'
import EventNoteIcon from '@mui/icons-material/EventNote'
import TitleIcon from '@mui/icons-material/Title'
import TimelapseIcon from '@mui/icons-material/Timelapse'

import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import TimePickerInputController from '@/components/ui/inputs/TimePickerInputController'

import { PlanMeetingFormData, usePlanMeetingForm } from '../model/usePlanMeetingForm'
import TextInputController from '@/components/ui/inputs/TextInputController'

export function PlanMeeting({
  expertiseId,
  patientId,
  setActiveStep,
}: {
  expertiseId: string
  patientId: string
  setActiveStep: Dispatch<SetStateAction<number>>
}) {
  useLingui()
  const { isCreatingExpertiseMeeting, rhForm, onSubmit } = usePlanMeetingForm({ expertiseId, patientId })
  const {
    formState: { isValid },
  } = rhForm

  return (
    <Stack height="100%">
      <Box flex={1} p={4}>
        <FormProvider {...rhForm}>
          <form>
            <List component="div" sx={{ maxWidth: 'sm', mx: 'auto' }}>
              <TitleField />
              <DateField />
              <TimeField />
            </List>
          </form>
        </FormProvider>
      </Box>
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2} pb={2} pl={3} pr={3}>
        <Button onClick={() => setActiveStep((prevStep) => prevStep - 1)} variant="neutral">
          <Trans>Back</Trans>
        </Button>
        <Stack direction="row" alignItems="center" justifyContent="end" spacing={3} sx={{ flex: 1 }}>
          <LoadingButton
            color="primary"
            disabled={!isValid}
            loading={isCreatingExpertiseMeeting}
            onClick={() => {
              rhForm.handleSubmit(onSubmit, (error) => {
                console.log(error)
              })()
            }}
            variant="contained"
          >
            <Trans>Confirm</Trans>
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

function TitleField() {
  const rhForm = useFormContext<PlanMeetingFormData>()
  return (
    <ListItem component="div">
      <ListItemAvatar>
        <Avatar>
          <TitleIcon />
        </Avatar>
      </ListItemAvatar>
      <TextInputController label={t`Title`} fullWidth name="title" control={rhForm.control} />
    </ListItem>
  )
}

function DateField() {
  const rhForm = useFormContext<PlanMeetingFormData>()
  return (
    <ListItem component="div">
      <ListItemAvatar>
        <Avatar>
          <EventNoteIcon />
        </Avatar>
      </ListItemAvatar>
      <Controller
        control={rhForm.control}
        name="date"
        render={({ field }) => (
          <DatePickerInput
            label={t`Date`}
            onChange={(date) => {
              rhForm.setValue('date', date?.format('YYYY-MM-DD') ?? '')
            }}
            value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
          />
        )}
      />
    </ListItem>
  )
}

function TimeField() {
  const rhForm = useFormContext<PlanMeetingFormData>()
  return (
    <ListItem component="div">
      <ListItemAvatar>
        <Avatar>
          <TimelapseIcon />
        </Avatar>
      </ListItemAvatar>
      <TimePickerInputController label={t`Time`} name="time" control={rhForm.control} />
    </ListItem>
  )
}
